enum APIErrorType {
  NotAuthenticated = 'NotAuthenticated', // Problem with JWT token
  NotAuthorized = 'NotAuthorized', // Permissions error, user is not authorized to access the resource.
  ClientError = 'ClientError', // Client error, something went wrong.
  ServerError = 'ServerError', // Server error, something went wrong.
  UnprocessableContent = 'UnprocessableContent', // Unprocessable Content - validation errors
}

/**
 * Custom error class for API errors. This helps use handle authentication errors and other errors in a consistent way.
 */
class APIError extends Error {
  status: number;
  statusText: string;
  type: APIErrorType;

  constructor(message: string | undefined, response: Response, request: Request) {
    super(message);

    this.name = `${request.method} ${request.url} ${response.status}`;
    Object.setPrototypeOf(this, APIError.prototype);

    if (response.status === 401 || JSON.parse(message ?? '{}')?.detail === 'Expired JWT') {
      this.type = APIErrorType.NotAuthenticated;
    } else if (response.status === 403) {
      this.type = APIErrorType.NotAuthorized;
    } else if (response.status === 422) {
      this.type = APIErrorType.UnprocessableContent;
    } else if (response.status >= 400 && response.status < 500) {
      this.type = APIErrorType.ClientError;
    } else {
      this.type = APIErrorType.ServerError;
    }

    this.status = response.status;
    this.statusText = response.statusText;
  }
}

/**
 * Reads the `error_code` the API puts in its error envelope
 * (`{ detail, status_code, error_code, request_id, extra, errors, timestamp }`).
 */
function getAPIErrorCode(error: unknown): string | undefined {
  if (!(error instanceof APIError)) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(error.message);

    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }

    const { error_code, code } = parsed as Record<string, unknown>;

    if (typeof error_code === 'string') return error_code;
    if (typeof code === 'string') return code;

    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Turns an error from an API call into a message to show the user. Reads the
 * `detail` of the API's error envelope when it is a string, or joins the `msg`
 * of each entry when it is a validation error list; any other `Error` yields
 * its own message. Everything else, including an unreadable body, yields the
 * fallback.
 */
function getAPIErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }

  if (!(error instanceof APIError)) {
    return error.message || fallback;
  }

  try {
    const parsed: unknown = JSON.parse(error.message);
    const detail = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>).detail : undefined;

    if (typeof detail === 'string' && detail) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>).msg : undefined))
        .filter((msg): msg is string => typeof msg === 'string' && msg.length > 0);

      if (messages.length > 0) {
        return messages.join(' ');
      }
    }

    return fallback;
  } catch {
    return fallback;
  }
}

export { APIError, APIErrorType, getAPIErrorCode, getAPIErrorMessage };
